/* ------------------------ Functional imports start ------------------------ */
import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse, type DetailResponse } from '../../../api/client'
import {
  fetchContract,
  fetchContracts,
  fetchBill,
  fetchBills,
  searchContracts,
  searchBills,
  type ContractFilters,
  type BillFilters,
} from '../api/contractApi'
import { type Contract, type Bill } from '../utils/types'
import { type PaginatedQueryParameters, type DetailQueryParameters } from '../../../api/hooks/utils/types'
import { useSmartListQuery, useSmartDetailQuery } from '../../../api/hooks/useSmartQueries'
import { contractKeys, billKeys } from '../utils/queryKeys'
/* ------------------------- Functional imports end ------------------------- */

/**
 * Hook that provides the available Contract instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useContracts(
  parameters: PaginatedQueryParameters<Contract, ContractFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Contract>> {
  return useSmartListQuery<Contract, ContractFilters>(
    {
      queryKeyFactory: contractKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchContracts(parameters, ids),
      elasticSearchFn: () => searchContracts(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch a single contract by ID or URL
 */
export function useContract(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Contract>,
  enabled = true,
): UseQueryResult<DetailResponse<Contract>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: contractKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchContract(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook that provides the available Bill instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useBills(
  parameters: PaginatedQueryParameters<Bill, BillFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Bill>> {
  return useSmartListQuery<Bill, BillFilters>(
    {
      queryKeyFactory: billKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchBills(parameters, ids),
      elasticSearchFn: () => searchBills(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch a single bill by ID or URL
 */
export function useBill(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Bill>,
  enabled = true,
): UseQueryResult<DetailResponse<Bill>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: billKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchBill(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook to fetch multiple contracts from Elasticsearch search results
 * Loads full contract data from the database for each contract ID
 * 
 * @param contractIds - Array of contract IDs from Elasticsearch search
 * @param expand - Fields to expand in the contract response
 * @param enabled - Whether to enable the queries
 * @returns Object with contracts, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { contracts, isLoading, hasErrors } = useContractsFromSearch(
 *   ['1', '2', '3'],
 *   ['offer', 'editors', 'shipping_address', 'billing_address']
 * )
 * ```
 */
export function useContractsFromSearch(
  contractIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: contractIds.map((id) => ({
      queryKey: contractKeys.detail(id, { expand }),
      queryFn: () => fetchContract(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract contracts from query results
  const contracts = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Contract)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = contractIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    contracts,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}

/**
 * Hook to fetch multiple bills from Elasticsearch search results
 * Loads full bill data from the database for each bill ID
 * 
 * @param billIds - Array of bill IDs from Elasticsearch search
 * @param expand - Fields to expand in the bill response
 * @param enabled - Whether to enable the queries
 * @returns Object with bills, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { bills, isLoading, hasErrors } = useBillsFromSearch(
 *   ['1', '2', '3'],
 *   ['contract', 'editors']
 * )
 * ```
 */
export function useBillsFromSearch(
  billIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: billIds.map((id) => ({
      queryKey: billKeys.detail(id, { expand }),
      queryFn: () => fetchBill(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract bills from query results
  const bills = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Bill)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = billIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    bills,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}
